import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import { Message as MessageObject } from "revolt.js";
import styled from "styled-components";
import { useTriggerEvents } from "preact-context-menu";
import { Text } from "preact-i18n";
import { memo } from "preact/compat";
import { useEffect, useMemo, useState } from "preact/hooks";

import { Category } from "@revoltchat/ui";

import { internalEmit } from "../../../lib/eventEmitter";
import { isTouchscreenDevice } from "../../../lib/isTouchscreenDevice";

import { QueuedMessage } from "../../../mobx/stores/MessageQueue";

import { I18nError } from "../../../context/Locale";

import { modalController } from "../../../controllers/modals/ModalController";
import Markdown from "../../markdown/Markdown";
import UserIcon from "../user/UserIcon";
import { Username } from "../user/UserShort";
import Attachment from "./attachments/Attachment";
import {
    ForwardBody,
    ForwardWrapper,
    MessageReply,
} from "./attachments/MessageReply";
import { Reactions } from "./attachments/Reactions";
import { MessageOverlayBar } from "./bars/MessageOverlayBar";
import Embed from "./embed/Embed";
import InviteList from "./embed/EmbedInvite";
import MessageBase, {
    MessageContent,
    MessageDetail,
    MessageInfo,
} from "./MessageBase";

interface Props {
    attachContext?: boolean;
    queued?: QueuedMessage;
    message: MessageObject;
    highlight?: boolean;
    contrast?: boolean;
    content?: Children;
    head?: boolean;
    hideReply?: boolean;
    channelLabel?: string;
    renderedAsPinned?: boolean;
    inSelectionMode?: boolean;
    selected?: boolean;
    onToggleSelection?: () => void;
}

const Message = observer(
    ({
        highlight,
        attachContext,
        message,
        contrast,
        content: replacement,
        head: preferHead,
        queued,
        hideReply,
        channelLabel,
        renderedAsPinned,
        inSelectionMode,
        selected,
        onToggleSelection,
    }: Props) => {
        const client = message.client;
        const user = message.author;

        const content = message.content;
        const head =
            preferHead || (message.reply_ids && message.reply_ids.length > 0);
        const history = useHistory();

        const userContext = attachContext
            ? useTriggerEvents("Menu", {
                  user: message.author_id,
                  contextualChannel: message.channel_id,
                  contextualMessage: message._id,
                  messageContext: renderedAsPinned ? "pinned" : undefined,
                  // eslint-disable-next-line
              })
            : undefined;

        const channel = message.channel_id
            ? client.channels.get(message.channel_id)
            : undefined;

        const openProfile = () =>
            modalController.push({
                type: "user_profile",
                user_id: message.author_id,
                channel,
                initialTab:
                    channel?.server_id &&
                    channel.server?.havePermission("AssignRoles")
                        ? "roles"
                        : undefined,
            });

        const handleUserClick = (e: MouseEvent) => {
            if (e.shiftKey && user?._id) {
                internalEmit(
                    "MessageBox",
                    "append",
                    `<@${user._id}>`,
                    "mention",
                );
            } else {
                openProfile();
            }
        };

        // ! FIXME(?): animate on hover
        const [mouseHovering, setAnimate] = useState(false);
        const [reactionsOpen, setReactionsOpen] = useState(false);
        useEffect(() => setAnimate(false), [replacement]);

        const isError = queued?.error !== undefined;
        if (isError) {
            return (
                <Category>
                    <I18nError error={queued.error} />
                </Category>
            );
        }

        const imagesAttachments = message.attachments?.filter(
            (msg) => msg.metadata.type === "Image",
        );
        const otherAttachments = message.attachments?.filter(
            (msg) => msg.metadata.type !== "Image",
        );

        const columns = useMemo(() => {
            if (imagesAttachments?.length === 2) return 2;
            if (imagesAttachments?.length === 1) return 1;
            return 3;
        }, [imagesAttachments]);

        return (
            <div id={renderedAsPinned ? `pinned-${message._id}` : message._id}>
                {!hideReply &&
                    message.reply_ids?.map((message_id, index) => (
                        <MessageReply
                            key={message_id}
                            index={index}
                            id={message_id}
                            channel={message.channel}
                        />
                    ))}
                <MessageRow
                    className={inSelectionMode ? "msg-selecting" : undefined}
                    onClick={() => inSelectionMode && onToggleSelection?.()}
                    onMouseEnter={() => setAnimate(true)}
                    onMouseLeave={() => setAnimate(false)}>
                    {inSelectionMode && (
                        <>
                            <style>{`.msg-selecting .msg-base-selecting *, .msg-selecting .msg-content-selecting * { pointer-events: none; } .msg-selecting .msg-base-selecting .overlay-bar *, .msg-selecting .msg-content-selecting .overlay-bar * { pointer-events: auto !important; }`}</style>
                            <SelectionCheckbox
                                onClick={(e: Event) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={onToggleSelection}
                                    aria-label={`Select message by ${
                                        user?.display_name ||
                                        user?.username ||
                                        "unknown"
                                    }`}
                                />
                            </SelectionCheckbox>
                        </>
                    )}
                    <MessageBase
                        pinned={!!message.pinned}
                        highlight={highlight}
                        head={
                            hideReply
                                ? false
                                : ((head &&
                                      !(
                                          message.reply_ids &&
                                          message.reply_ids.length > 0
                                      )) ??
                                  false)
                        }
                        contrast={contrast}
                        sending={typeof queued !== "undefined"}
                        mention={message.isMentioned()}
                        flexGrow={1}
                        className={
                            inSelectionMode ? "msg-base-selecting" : undefined
                        }
                        {...(attachContext
                            ? useTriggerEvents("Menu", {
                                  message,
                                  contextualChannel: message.channel_id,
                                  queued,
                                  messageContext: renderedAsPinned
                                      ? "pinned"
                                      : undefined,
                              })
                            : undefined)}>
                        <MessageInfo click={typeof head !== "undefined"}>
                            {head ? (
                                <UserIcon
                                    className="avatar"
                                    url={message.generateMasqAvatarURL()}
                                    override={
                                        message.webhook?.avatar
                                            ? message.client.generateFileURL({
                                                  tag: "avatars",
                                                  _id: message.webhook.avatar,
                                              })
                                            : undefined
                                    }
                                    target={user}
                                    size={36}
                                    onClick={handleUserClick}
                                    animate={mouseHovering}
                                    {...(userContext as any)}
                                    showServerIdentity
                                />
                            ) : (
                                <MessageDetail
                                    message={message}
                                    position="left"
                                />
                            )}
                        </MessageInfo>
                        <MessageContent
                            role="button"
                            data-as-button={renderedAsPinned}
                            tabIndex={renderedAsPinned ? "0" : undefined}
                            className={
                                inSelectionMode
                                    ? "msg-content-selecting"
                                    : undefined
                            }
                            onClick={(e: MouseEvent) => {
                                if (inSelectionMode) return;
                                const elem = e.target as HTMLElement;
                                const isTagExcluded = [
                                    "IMG",
                                    "VIDEO",
                                    "A",
                                ].includes(elem.tagName);
                                const isClickCaptured = elem.hasAttribute(
                                    "data-click-captured",
                                );
                                if (
                                    renderedAsPinned &&
                                    !isTagExcluded &&
                                    !isClickCaptured
                                ) {
                                    history.push(message.path);
                                }
                            }}>
                            {head && (
                                <span className="detail">
                                    <Username
                                        data-click-captured
                                        user={user}
                                        className="author"
                                        showServerIdentity
                                        onClick={handleUserClick}
                                        masquerade={message.masquerade!}
                                        override={message.webhook?.name}
                                        {...userContext}
                                    />
                                    <MessageDetail
                                        message={message}
                                        position="top"
                                    />
                                    {channelLabel && (
                                        <ChannelLabel>
                                            <Text
                                                id="app.main.channel.search.in_channel_label"
                                                fields={{
                                                    channel: channelLabel,
                                                }}
                                            />
                                        </ChannelLabel>
                                    )}
                                </span>
                            )}
                            <ForwardWrapper
                                head={head}
                                active={!!message.forward_id}>
                                {message.forward_id && (
                                    <MessageReply
                                        type="forward"
                                        index={0}
                                        forwardId={message.forward_id}
                                        forwardChannelId={
                                            message.forward_channel
                                        }
                                        forwardAuthorId={message.forward_author}
                                        channel={message.channel}
                                    />
                                )}
                                <ForwardBody active={!!message.forward_id}>
                                    {replacement ??
                                        (content && (
                                            <Markdown
                                                content={content}
                                                channel={message.channel}
                                            />
                                        ))}
                                    {!queued && (
                                        <InviteList message={message} />
                                    )}
                                    {!!imagesAttachments?.length &&
                                        (() => {
                                            const photos =
                                                imagesAttachments.map(
                                                    (att) => ({
                                                        src: client.generateFileURL(
                                                            att,
                                                        )!,
                                                        key:
                                                            att._id ||
                                                            client.generateFileURL(
                                                                att,
                                                            )!,
                                                        rawAttachment: att,
                                                    }),
                                                );

                                            let columnCount = 3;
                                            if (photos.length === 1)
                                                columnCount = 1;
                                            else if (photos.length === 2)
                                                columnCount = 2;

                                            return (
                                                <div
                                                    style={{
                                                        width: "100%",
                                                        maxWidth: "520px",
                                                        margin: "4px 0",
                                                        columnCount,
                                                        columnGap: "6px",
                                                    }}
                                                    className="message-flex-gallery">
                                                    {photos.map(
                                                        (photo, index) => (
                                                            <div
                                                                key={photo.key}
                                                                style={{
                                                                    position:
                                                                        "relative",
                                                                    display:
                                                                        "inline-block",
                                                                    width: "100%",
                                                                    marginBottom:
                                                                        "6px",
                                                                    borderRadius:
                                                                        "8px",
                                                                    overflow:
                                                                        "hidden",
                                                                }}
                                                                className="flex-column-attachment-wrapper">
                                                                <Attachment
                                                                    allAttachments={
                                                                        message.attachments
                                                                    }
                                                                    attachment={
                                                                        photo.rawAttachment
                                                                    }
                                                                    hasContent={
                                                                        index >
                                                                            0 ||
                                                                        (content
                                                                            ? content.length >
                                                                              0
                                                                            : false)
                                                                    }
                                                                />
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    {otherAttachments?.map(
                                        (attachment, index) => (
                                            <Attachment
                                                key={index}
                                                allAttachments={
                                                    message.attachments
                                                }
                                                attachment={attachment}
                                                hasContent={
                                                    index > 0 ||
                                                    (content
                                                        ? content.length > 0
                                                        : false)
                                                }
                                            />
                                        ),
                                    )}
                                    {message.embeds?.map((embed, index) => (
                                        <Embed
                                            key={index}
                                            embed={embed}
                                            allEmbeds={message.embeds}
                                        />
                                    ))}
                                </ForwardBody>
                            </ForwardWrapper>
                            <Reactions message={message} />
                            {(mouseHovering || reactionsOpen) &&
                                !replacement &&
                                !isTouchscreenDevice && (
                                    <MessageOverlayBar
                                        className="overlay-bar"
                                        renderedAsPinned={renderedAsPinned}
                                        reactionsOpen={reactionsOpen}
                                        setReactionsOpen={setReactionsOpen}
                                        message={message}
                                        queued={queued}
                                    />
                                )}
                        </MessageContent>
                    </MessageBase>
                </MessageRow>
            </div>
        );
    },
);

const MessageRow = styled.div`
    display: flex;
    width: 100%;
    flex: 1;

    &:hover {
        background: var(--hover);

        time {
            opacity: 1;
        }

        .system-message-icon {
            display: none;
        }

        .edited-hide-one-hover {
            display: none;
        }
    }
`;

const ChannelLabel = styled.span`
    font-weight: 500;
    color: var(--secondary-foreground);
`;

const SelectionCheckbox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    flex-shrink: 0;
    padding-top: 2px;
    cursor: pointer;

    input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: var(--accent);
    }
`;

export default memo(Message);
